import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PermissionsForm from './PermissionsForm';
import { getRoleById as getRoleByIdApi, updateRole as updateRoleApi } from '../../../models/roleModel';

function RoleEdit() {
  const [roleName, setRoleName] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingRole, setFetchingRole] = useState(true);
  const navigate = useNavigate();
  const { id } = useParams();

  const fetchRole = useCallback(async () => {
    try {
      setFetchingRole(true);
      const response = await getRoleByIdApi(id);
      
      if (response.status === 1) {
        const roleData = response.data.row; // ApiRoles returns data.row
        setRoleName(roleData.role_name);
        
        if (roleData.permissions) {
          try {
            const parsedPermissions = JSON.parse(roleData.permissions);
            setPermissions(Array.isArray(parsedPermissions) ? parsedPermissions : []);
          } catch (e) {
            console.error('Failed to parse permissions:', e);
            setPermissions([]);
            toast.warn('Could not parse permissions for this role.');
          }
        } else {
          setPermissions([]);
        }
      } else {
        toast.error(response.message || 'Failed to load role details');
        navigate('/dashboard/roles');
      }
    } catch (error) {
      console.error('Error fetching role:', error);
      toast.error(error.response?.data?.message || error.message || 'An error occurred while loading role details');
      navigate('/dashboard/roles');
    } finally {
      setFetchingRole(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchRole();
  }, [fetchRole]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!roleName.trim()) {
      toast.error('Role name is required');
      return;
    }

    try {
      setLoading(true);
      const response = await updateRoleApi(id, {
        role_name: roleName,
        permissions: permissions // Send as a JS array
      });
      
      if (response && response.status == 1) {
        toast.success('Role updated successfully!');
        navigate('/dashboard/roles');
      } else {
        toast.error(response.message || 'Failed to update role');
      }
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error(error.response?.data?.message || error.message || 'An error occurred while updating the role');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  if (fetchingRole) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <div className="flex justify-center items-center space-x-2">
            <svg className="animate-spin h-5 w-5 text-[#0087D4]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading role...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header with Back Navigation */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-[#0087D4] hover:text-blue-600 transition-colors mb-4"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Edit Role</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Column: Role Details */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Role Details</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="role_name" className="block text-sm font-medium text-gray-700">
                    Role Name
                  </label>
                  <input
                    type="text"
                    id="role_name"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm"
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value)}
                    placeholder="Enter role name"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
              <div className="flex flex-col gap-3">
                <button 
                  type="submit" 
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#0087D4] hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0087D4] w-full"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating...
                    </>
                  ) : 'Update Role'}
                </button>
                <button 
                  type="button" 
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0087D4] w-full"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          {/* Right Column: Permissions */}
          <div className="lg:col-span-3">
            <PermissionsForm 
              permissions={permissions} 
              setPermissions={setPermissions} 
            />
          </div>
        </div>
      </form>
    </div>
  );
}

export default RoleEdit;